import { create } from 'zustand'
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware'
import { flutterBridge } from '@/bridge/flutter-bridge'

interface Subscription {
  status: 'none' | 'trial' | 'active' | 'cancelled' | 'past_due' | 'expired'
  trial_ends_at?: string
  current_period_end?: string
  trial_days_remaining?: number
  is_premium?: boolean
}

interface User {
  id: string
  phone_number: string
  name: string
  onboarding_completed: boolean
  situations_selected: string[]
  push_enabled: boolean
  subscription?: Subscription
  child_profile?: {
    id: string
    name: string
    date_of_birth: string
    age_months: number
    sensory_profile: Record<string, unknown>
  }
}

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean

  // Actions
  setTokens: (accessToken: string, refreshToken: string) => void
  setUser: (user: User) => void
  logout: () => void
  setLoading: (loading: boolean) => void
}

// Key for storing refresh token in Flutter secure storage
const SECURE_REFRESH_TOKEN_KEY = 'mellomap_refresh_token'

// Custom storage that uses Flutter secure storage for refresh tokens when in Flutter
const createHybridStorage = (): StateStorage => {
  const isFlutter = flutterBridge.isInFlutter

  // In browser: use synchronous localStorage to avoid hydration delay
  if (!isFlutter) {
    return {
      getItem: (name: string) => localStorage.getItem(name),
      setItem: (name: string, value: string) => localStorage.setItem(name, value),
      removeItem: (name: string) => localStorage.removeItem(name),
    }
  }

  // In Flutter: use async hybrid storage (secure storage for refresh tokens)
  return {
    getItem: async (name: string): Promise<string | null> => {
      const localData = localStorage.getItem(name)
      if (!localData) return null

      try {
        const state = JSON.parse(localData)
        const secureRefreshToken = await flutterBridge.getSecure(SECURE_REFRESH_TOKEN_KEY)
        if (secureRefreshToken && state.state) {
          state.state.refreshToken = secureRefreshToken
        }
        return JSON.stringify(state)
      } catch {
        return localData
      }
    },

    setItem: async (name: string, value: string): Promise<void> => {
      try {
        const parsed = JSON.parse(value)
        const refreshToken = parsed.state?.refreshToken

        if (refreshToken) {
          await flutterBridge.storeSecure(SECURE_REFRESH_TOKEN_KEY, refreshToken)
          parsed.state.refreshToken = null
        }

        localStorage.setItem(name, JSON.stringify(parsed))
      } catch {
        localStorage.setItem(name, value)
      }
    },

    removeItem: async (name: string): Promise<void> => {
      await flutterBridge.deleteSecure(SECURE_REFRESH_TOKEN_KEY)
      localStorage.removeItem(name)
    },
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: true,

      setTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
          isAuthenticated: true,
        }),

      setUser: (user) =>
        set({
          user,
          isAuthenticated: true,
        }),

      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        }),

      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'mellomap-auth',
      storage: createJSONStorage(() => createHybridStorage()),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
